import { FocusSummaryCard } from '@/shared/components';
import { useFacilityScope } from '@/shared/lib/hooks/use-facility-scope';
import { useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import type { FC } from 'react';
import { getCreatedAlertsQueryOptions } from '../api/get-created-alerts.query';

type AlertsCreatedCardProps = {
  dateRange: {
    from: Dayjs;
    to: Dayjs;
  };
};

export const AlertsCreatedCard: FC<AlertsCreatedCardProps> = ({
  dateRange,
}) => {
  const { scopedFacilityId } = useFacilityScope();
  const {
    data: createdAlerts,
    isLoading,
    isFetching,
  } = useQuery(
    getCreatedAlertsQueryOptions({
      from: dateRange.from.format('YYYY-MM-DD'),
      to: dateRange.to.format('YYYY-MM-DD'),
      facilitiesIds: scopedFacilityId ? [scopedFacilityId] : undefined,
    })
  );

  if (!isLoading && !createdAlerts) {
    return (
      <FocusSummaryCard
        value="N/A"
        title="Created Alerts"
        icon="warning-triangle"
        loading={false}
      />
    );
  }

  const sparklinePoints = createdAlerts?.sparkline.points || [];

  const chartData = {
    labels: sparklinePoints.map((_, index) => index.toString()),
    datasets: [
      {
        data: sparklinePoints.map((point) => point.value),
        borderColor: '#165BAA',
        backgroundColor: 'transparent',
        fill: false,
      },
    ],
  };

  const indicator =
    createdAlerts?.changePct != null
      ? {
          value: createdAlerts.changePct,
          negativeValue: createdAlerts.changePct < 0,
          negativeAsPositive: true,
        }
      : undefined;

  return (
    <FocusSummaryCard
      showChart
      title="Created Alerts"
      icon="warning-triangle"
      tooltipMessage="Number of new alerts created during the selected period"
      indicator={indicator}
      chart={{
        data: chartData,
      }}
      value={createdAlerts?.count.toString() ?? 'N/A'}
      loading={isFetching}
    />
  );
};
